import { z } from "zod";
import { event, request } from "./defs.ts";
import { isValidSemver, MAX_SEMVER_LENGTH } from "./semver.ts";

/**
 * Remote Desktop updates (V1). The website only REQUESTS an update to an already published, signed release; the Desktop
 * app decides from its own trusted feed what it installs, verifies it with electron-updater / Squirrel.Mac and restarts.
 *
 *   browser ──machine.update.*──► server (ownership, session, snapshot, job) ──update.*──► agent runtime ──► Electron main
 *   browser ◄──machine.update──── server (persists the job state machine)   ◄──update.progress / update.sync──┘
 *
 * Nothing here carries a URL, a path, a command, a token of the user or free text from the Machine: only ids, enums,
 * counts and SemVer strings.
 */

/** Handshake capability: the client can be updated remotely with this protocol. */
export const DESKTOP_UPDATE_CAPABILITY = "desktop-update-v1";

export const updateChannelSchema = z.enum(["stable", "beta"]);
export type UpdateChannel = z.infer<typeof updateChannelSchema>;

export const updateArchSchema = z.enum(["arm64", "x64"]);
export type UpdateArch = z.infer<typeof updateArchSchema>;

/** A strict SemVer string (bounded). */
export const semverSchema = z
  .string()
  .max(MAX_SEMVER_LENGTH)
  .refine((v) => isValidSemver(v), "invalid SemVer version");

const uuid = z.uuid();
const count = z.number().int().nonnegative().max(100_000);

/**
 * Work on the Machine that a restart interrupts. Counts only (never ids, names or content). Every kind is detected
 * reliably by the runtime; whatever it cannot account for makes the Desktop abort instead of guessing.
 */
export const updateActivitySchema = z.strictObject({
  /** Interactive terminals that are still running. */
  terminals: count,
  /** AI agent runs in progress. */
  agentRuns: count,
  /** Commands of an agent run that are executing right now. */
  runningCommands: count,
  /** Commands waiting for the user's approval (they are dropped by a restart). */
  pendingApprovals: count,
  /** File uploads in progress. */
  transfers: count,
  /** Long-running operations in progress (Git installation, project clone, worktree setup). */
  operations: count,
});
export type UpdateActivity = z.infer<typeof updateActivitySchema>;

export const EMPTY_ACTIVITY: UpdateActivity = {
  terminals: 0,
  agentRuns: 0,
  runningCommands: 0,
  pendingApprovals: 0,
  transfers: 0,
  operations: 0,
};

export const activityTotal = (a: UpdateActivity): number =>
  a.terminals + a.agentRuns + a.runningCommands + a.pendingApprovals + a.transfers + a.operations;

/** Why an update did not complete. Fixed codes only: the Machine never sends free text to the server. */
export const UPDATE_ERROR_CODES = [
  /** The feed offers nothing newer for this channel / architecture. */
  "no_update",
  /** The feed now selects a different version than the one the user confirmed. */
  "target_changed",
  /** The Desktop's channel differs from the one the server sent (sync still in flight). */
  "channel_mismatch",
  /** Another update is already being downloaded or installed. */
  "update_in_progress",
  /** The confirmation snapshot is unknown, already used or expired. */
  "snapshot_invalid",
  /** New work started after the user confirmed. */
  "activity_changed",
  /** The Desktop could not determine the running work reliably. */
  "activity_unknown",
  /** The update feed could not be read or was invalid. */
  "feed_unavailable",
  "download_failed",
  /** SHA-512 or code-signature validation failed. */
  "integrity_failed",
  "install_failed",
  /** The Machine came back with its previous version. */
  "update_not_applied",
  /** The Machine came back with a version that is neither the old nor the target version. */
  "unexpected_version",
  /** The Desktop app stopped before the update reached the install step. */
  "interrupted",
  /** No progress within the allowed time. */
  "timeout",
  /** The Machine did not come back in time after the restart. */
  "reconnect_timeout",
  /** Updates are not available in this build (development, unpackaged, disabled). */
  "updater_unavailable",
] as const;
export const updateErrorCodeSchema = z.enum(UPDATE_ERROR_CODES);
export type UpdateErrorCode = z.infer<typeof updateErrorCodeSchema>;

/**
 * Server-side job state machine. Forward only; every non-terminal state may end in `failed` or `unclear`.
 * `succeeded` is ONLY set when the Machine is authenticated again and reports exactly the target version.
 */
export const UPDATE_JOB_STATUSES = [
  "requested",
  "downloading",
  "verifying",
  "preparing",
  "installing",
  "awaiting_reconnect",
  "succeeded",
  "failed",
  "unclear",
] as const;
export const updateJobStatusSchema = z.enum(UPDATE_JOB_STATUSES);
export type UpdateJobStatus = z.infer<typeof updateJobStatusSchema>;

export const TERMINAL_JOB_STATUSES: ReadonlySet<UpdateJobStatus> = new Set([
  "succeeded",
  "failed",
  "unclear",
]);
export const isTerminalJobStatus = (s: UpdateJobStatus): boolean => TERMINAL_JOB_STATUSES.has(s);

const ORDER: Record<UpdateJobStatus, number> = {
  requested: 0,
  downloading: 1,
  verifying: 2,
  preparing: 3,
  installing: 4,
  awaiting_reconnect: 5,
  succeeded: 6,
  failed: 6,
  unclear: 6,
};

/**
 * Is `from -> to` a legal step? Forward only (a phase may repeat, e.g. download progress), `succeeded`/`failed` are final,
 * `awaiting_reconnect` only follows the install steps (`preparing`, `installing`). `succeeded` is allowed from every open state because the caller only
 * sets it on hard evidence (authenticated reconnect with the target version), never because of a phase report.
 */
export function canTransition(from: UpdateJobStatus, to: UpdateJobStatus): boolean {
  // `unclear` (a timeout without evidence) may still be resolved by hard evidence from a later authenticated reconnect.
  if (from === "unclear") return to === "succeeded" || to === "failed";
  if (isTerminalJobStatus(from)) return false;
  if (to === "failed" || to === "unclear" || to === "succeeded") return true;
  if (to === "awaiting_reconnect")
    return from === "preparing" || from === "installing" || from === "awaiting_reconnect";
  return ORDER[to] >= ORDER[from];
}

/** Phases the Desktop reports itself (the server derives `requested`, `awaiting_reconnect` and the outcomes). */
export const desktopUpdatePhaseSchema = z.enum([
  "downloading",
  "verifying",
  "preparing",
  "installing",
  "failed",
]);
export type DesktopUpdatePhase = z.infer<typeof desktopUpdatePhaseSchema>;

/** What the Desktop's updater reports about itself (no URLs, no paths). */
export const updaterInfoSchema = z.strictObject({
  /** False in development / unpackaged / disabled builds: remote updates are refused. */
  supported: z.boolean(),
  installedVersion: semverSchema,
  arch: updateArchSchema,
  channel: updateChannelSchema,
  /** The updater is downloading or installing right now (a new job would be refused). */
  busy: z.boolean(),
  /** A verified update that is already downloaded (no second download needed). */
  downloadedVersion: semverSchema.nullable(),
});
export type UpdaterInfo = z.infer<typeof updaterInfoSchema>;

export const snapshotTokenSchema = z
  .string()
  .min(16)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/);

/** The Desktop's channel as it knows it: `pending` = changed locally, not yet confirmed by the server. */
export const channelReportSchema = z.strictObject({
  channel: updateChannelSchema,
  revision: z.number().int().nonnegative(),
  pending: z.boolean(),
});
export type ChannelReport = z.infer<typeof channelReportSchema>;

export const startResultSchema = z.union([
  z.strictObject({ accepted: z.literal(true) }),
  z.strictObject({ accepted: z.literal(false), reason: updateErrorCodeSchema }),
]);
export type StartResult = z.infer<typeof startResultSchema>;

// ---- server -> agent (never routable from a browser) ----------------------------------------------------------------

export const updateStatusResponseSchema = z.strictObject({
  /** Opaque handle of the activity snapshot the Machine keeps (the ids it stands for never leave the Machine). */
  snapshotToken: snapshotTokenSchema,
  expiresInMs: z
    .number()
    .int()
    .positive()
    .max(10 * 60_000),
  activity: updateActivitySchema,
  updater: updaterInfoSchema,
});
export type UpdateStatusResponse = z.infer<typeof updateStatusResponseSchema>;

export const updateStartRequestSchema = z.strictObject({
  jobId: uuid,
  snapshotToken: snapshotTokenSchema,
  /** What the user confirmed. The Desktop re-selects from its own feed and refuses when the result differs. */
  channel: updateChannelSchema,
  targetVersion: semverSchema,
});
export type UpdateStartRequest = z.infer<typeof updateStartRequestSchema>;

export const channelAssignmentSchema = z.strictObject({
  channel: updateChannelSchema,
  revision: z.number().int().positive(),
});
export type ChannelAssignment = z.infer<typeof channelAssignmentSchema>;

export const desktopUpdateCommands = {
  /** Updater + activity state, and a short-lived activity snapshot on the Machine. */
  "system/update.status": request("system", "update.status", z.strictObject({}), updateStatusResponseSchema),
  /** Install the newest allowed release. No URL, no path, no file: only the job id and what the user confirmed. */
  "system/update.start": request("system", "update.start", updateStartRequestSchema, startResultSchema),
  /** The server's channel for this Machine (monotonic revision; the Desktop ignores anything older than it knows). */
  "system/update.setChannel": request(
    "system",
    "update.setChannel",
    channelAssignmentSchema,
    channelReportSchema,
  ),
} as const;

// ---- agent -> server (consumed by the server, NEVER forwarded to browsers) -----------------------------------------

export const updateProgressSchema = z.strictObject({
  jobId: uuid,
  phase: desktopUpdatePhaseSchema,
  /** Download progress 0..100 (downloading only). */
  percent: z.number().int().min(0).max(100).optional(),
  /** Required with `failed`. */
  errorCode: updateErrorCodeSchema.optional(),
  /** Only with `activity_changed`: the work that is running now. */
  activity: updateActivitySchema.optional(),
});
export type UpdateProgress = z.infer<typeof updateProgressSchema>;

export const updateSyncSchema = z.strictObject({
  channel: channelReportSchema,
  /** False in builds without a working updater (development, unpackaged, disabled). */
  updaterSupported: z.boolean(),
  /**
   * The remote job the Desktop is still working on, if any. The OUTCOME of a job is never taken from here: the server
   * decides it from the version of the authenticated handshake after the restart.
   */
  job: z
    .strictObject({
      jobId: uuid,
      phase: desktopUpdatePhaseSchema,
      errorCode: updateErrorCodeSchema.optional(),
    })
    .nullable(),
});
export type UpdateSync = z.infer<typeof updateSyncSchema>;

export const desktopUpdateAgentEvents = {
  "system/update.progress": event("system", "update.progress", updateProgressSchema),
  /**
   * Sent after every authentication and whenever the channel or the job phase changes. A locally changed channel travels
   * here too (`channel.pending`), so there is exactly one synchronisation path.
   */
  "system/update.sync": event("system", "update.sync", updateSyncSchema),
} as const;

export type ParsedDesktopUpdateEvent =
  { type: "update.progress"; payload: UpdateProgress } | { type: "update.sync"; payload: UpdateSync };

/** True for every update event type, valid or not: they never take the generic browser fan-out. */
export const isDesktopUpdateEventType = (channel: string, type: string): boolean =>
  channel === "system" && (type === "update.progress" || type === "update.sync");

/** A validated update event from an agent, or null for anything else / anything invalid. */
export function parseDesktopUpdateEvent(msg: {
  channel: string;
  type: string;
  payload: unknown;
}): ParsedDesktopUpdateEvent | null {
  if (msg.channel !== "system") return null;
  if (msg.type === "update.progress") {
    const p = updateProgressSchema.safeParse(msg.payload);
    return p.success ? { type: "update.progress", payload: p.data } : null;
  }
  if (msg.type === "update.sync") {
    const p = updateSyncSchema.safeParse(msg.payload);
    return p.success ? { type: "update.sync", payload: p.data } : null;
  }
  return null;
}

// ---- browser <-> server ---------------------------------------------------------------------------------------------

const versionText = z.string().max(MAX_SEMVER_LENGTH);
const isoText = z.string().max(40);

export const updateJobViewSchema = z.strictObject({
  jobId: uuid,
  status: updateJobStatusSchema,
  progress: z.number().int().min(0).max(100).nullable(),
  channel: updateChannelSchema,
  fromVersion: versionText,
  targetVersion: versionText,
  /** The version the Machine reported after the restart (null until it reconnected). */
  installedVersion: versionText.nullable(),
  errorCode: updateErrorCodeSchema.nullable(),
  /** The work the user confirmed, or (after `activity_changed`) what was running at the check. */
  activity: updateActivitySchema,
  createdAt: isoText,
  updatedAt: isoText,
  finishedAt: isoText.nullable(),
});
export type UpdateJobView = z.infer<typeof updateJobViewSchema>;

/** Why the Machine cannot be updated from the website right now (null = it can, if an update exists). */
export const remoteUpdateBlockerSchema = z.enum([
  /** Not connected. Remote updates of offline Machines are not part of V1. */
  "offline",
  /** A Desktop version without the remote updater: one manual update inside the app is needed. */
  "legacy_client",
  /** The `racler` CLI agent, or a client that never reported what it is. */
  "unsupported_client",
  "unsupported_arch",
  /** The Desktop reported that updates are unavailable in its build (development / unpackaged / disabled). */
  "updater_unavailable",
]);
export type RemoteUpdateBlocker = z.infer<typeof remoteUpdateBlockerSchema>;

/** Feed trouble that affects THIS Machine's channel and architecture. */
export const feedProblemSchema = z.enum(["unavailable", "access_denied", "invalid"]);
export type FeedProblem = z.infer<typeof feedProblemSchema>;

export const updateAvailabilitySchema = z.enum(["up_to_date", "optional", "required", "unknown"]);
export type UpdateAvailability = z.infer<typeof updateAvailabilitySchema>;

export const machineUpdateViewSchema = z.strictObject({
  machineId: uuid,
  online: z.boolean(),
  /** The last client report (null = never reported since the server stores it). */
  client: z
    .strictObject({
      product: z.string().max(64),
      version: versionText,
      arch: z.string().max(32),
      remoteUpdates: z.boolean(),
      reportedAt: isoText.nullable(),
    })
    .nullable(),
  /** The server-side preference (the source of truth). */
  channel: updateChannelSchema,
  /** False while the connected Desktop has not confirmed the current channel revision. */
  channelSynced: z.boolean(),
  /** Newest release this Machine may install on its channel and architecture. */
  latest: z
    .strictObject({ version: versionText, channel: updateChannelSchema, releaseDate: isoText.nullable() })
    .nullable(),
  availability: updateAvailabilitySchema,
  /** The server-enforced minimum Desktop version, when one is configured. */
  minimumVersion: versionText.nullable(),
  feedProblem: feedProblemSchema.nullable(),
  remoteBlocker: remoteUpdateBlockerSchema.nullable(),
  /** The active job, or the most recent finished one (kept visible for a while). */
  job: updateJobViewSchema.nullable(),
});
export type MachineUpdateView = z.infer<typeof machineUpdateViewSchema>;

export const updatePrepareResponseSchema = z.strictObject({
  snapshotId: uuid,
  expiresAt: isoText,
  installedVersion: versionText,
  targetVersion: versionText,
  channel: updateChannelSchema,
  activity: updateActivitySchema,
  /** The target is already downloaded and verified on the Machine. */
  alreadyDownloaded: z.boolean(),
});
export type UpdatePrepareResponse = z.infer<typeof updatePrepareResponseSchema>;

/** Browser -> server (answered by the server; envelope WITHOUT machineId, ownership is checked in SQL). */
export const desktopUpdateServerRequests = {
  "system/machine.updates": request(
    "system",
    "machine.updates",
    z.strictObject({}),
    z.strictObject({ machines: z.array(machineUpdateViewSchema).max(500) }),
  ),
  /** Store the channel of ONE Machine. Never installs anything. */
  "system/machine.update.setChannel": request(
    "system",
    "machine.update.setChannel",
    z.strictObject({ machineId: uuid, channel: updateChannelSchema }),
    z.strictObject({ machine: machineUpdateViewSchema }),
  ),
  /** A fresh, time-limited status snapshot for the confirmation dialog. */
  "system/machine.update.prepare": request(
    "system",
    "machine.update.prepare",
    z.strictObject({ machineId: uuid }),
    updatePrepareResponseSchema,
  ),
  /** Start the confirmed update. `requestId` makes retries and double clicks idempotent. */
  "system/machine.update.start": request(
    "system",
    "machine.update.start",
    z.strictObject({
      machineId: uuid,
      snapshotId: uuid,
      requestId: uuid,
      /** The user explicitly accepted that the listed work may be stopped. */
      acknowledgeInterruption: z.literal(true),
    }),
    z.strictObject({ job: updateJobViewSchema }),
  ),
} as const;

/** Server -> browser: the update view of one Machine changed (sent only to its owner). */
export const desktopUpdateServerEvents = {
  "system/machine.update": event(
    "system",
    "machine.update",
    z.strictObject({ machine: machineUpdateViewSchema }),
  ),
} as const;
